'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { HELP_URLS } from 'help/constants/URLs'
import { ClientLabel } from 'labels/client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMemo } from 'react'
import { RETURN_TYPES } from 'shipping-and-returns/constants/returnTypes'
import { ContentContainer } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/ContentContainer/ContentContainer'
import { sendOnClickDroppoint } from 'shipping-and-returns/utils/analytics/analyticsEvents'
import { getReturnsUrlByReturnType } from 'shipping-and-returns/utils/getReturnsUrlByReturnType/getReturnsUrlByReturnType'

import texts from 'fukku/styles/texts.module.scss'

interface DropPointContentProps {
	readonly isFindDropointLink: boolean
	readonly isBlocked: boolean
	readonly hasSecondReturnsBlock: boolean
	readonly hasHomePickUpRequest: boolean
	readonly orderId?: string
	readonly purchaseId?: string
	readonly isDroppointMethodTempDisabled: boolean
}
export function DropPointContent({
	isFindDropointLink,
	isBlocked,
	hasSecondReturnsBlock,
	hasHomePickUpRequest,
	orderId,
	purchaseId,
	isDroppointMethodTempDisabled,
}: DropPointContentProps) {
	const { t } = useLabels()
	const { enabled: isNewHelpEnabled } = useFeatureFlag(featureFlagKeys.HelpPage)

	const requestReturnButtonKey =
		'myPurchases.online.details.returns.requestReturn.button'

	const actionHref = useMemo(
		() =>
			isFindDropointLink
				? HELP_URLS.DROPPOINT_RETURN
				: getReturnsUrlByReturnType({
						returnType: RETURN_TYPES.DROPPOINT,
						orderId,
						purchaseId,
					}),
		[orderId, purchaseId]
	)

	const handleDropPointAction = (_e: React.MouseEvent) => {
		sendOnClickDroppoint()
	}

	if (isBlocked) {
		return (
			<ContentContainer
				description={t('myReturns.returnOptions.blocked.bodycopy')}
			/>
		)
	}

	if (isDroppointMethodTempDisabled) {
		return (
			<ContentContainer
				description={<p>{t('myReturns.returnUnavailable.bodycopy')}</p>}
				action={{
					isDisabled: true,
					dataTestId: requestReturnButtonKey,
					text: t(requestReturnButtonKey),
				}}
			/>
		)
	}

	return (
		<ContentContainer
			description={
				<>
					<p>
						<ClientLabel>
							{t(
								isFindDropointLink
									? 'myPurchases.online.details.returns.dropPoint.noRequest.bodycopy'
									: 'myPurchases.online.details.returns.dropPoint.withRequest.bodycopy'
							)}
						</ClientLabel>
					</p>
					{hasSecondReturnsBlock && (
						<p
							data-testid='myReturns.returnOptions.dropPoint.blockingInfo'
							className={texts.bodyMRegular}
						>
							<ClientLabel>
								{t(
									hasHomePickUpRequest
										? 'myReturns.returnOptions.infoBlocking.several.bodycopy'
										: 'myReturns.returnOptions.infoBlocking.bodycopy'
								)}
							</ClientLabel>
						</p>
					)}
				</>
			}
			action={{
				onClick: handleDropPointAction,
				dataTestId: isFindDropointLink
					? 'myPurchases.online.details.returns.seeInstructions.button'
					: requestReturnButtonKey,
				text: t(
					isFindDropointLink
						? 'myPurchases.online.details.returns.seeInstructions.button'
						: requestReturnButtonKey
				),
				linkAttributes: {
					href: actionHref,
					withLegacyLocale: !isNewHelpEnabled && isFindDropointLink,
					...(isFindDropointLink && { linkProps: { target: '_blank' } }),
				},
			}}
		/>
	)
}
